import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Users, Lock } from "lucide-react";
import profileImage from "@/assets/profile.jpg";

const INVITATION_DURATION = 60; // 1 minuto
const COOKIE_NAME = "invite_accessed";

const Invite = () => {
  const [timeLeft, setTimeLeft] = useState<number>(INVITATION_DURATION);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(true);
  const [hasClicked, setHasClicked] = useState<boolean>(false);

  useEffect(() => {
    // Verificar si ya se hizo clic en el botón (esto sí debe persistir)
    const clickedBefore = document.cookie
      .split('; ')
      .find(row => row.startsWith('invite_clicked='));

    if (clickedBefore) {
      setHasClicked(true);
    }

    // Verificar si hay una cookie de tiempo de inicio
    const startTimeCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('invite_start_time='));

    let startTime;
    
    if (startTimeCookie) {
      // Si existe, recuperar el tiempo de inicio
      startTime = parseInt(startTimeCookie.split('=')[1]);
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = INVITATION_DURATION - elapsed;
      
      if (remaining <= 0) {
        // Ya expiró
        setIsExpired(true);
        setIsFirstVisit(false);
        return;
      } else {
        // Aún hay tiempo, continuar desde donde se quedó
        setTimeLeft(remaining);
        setIsFirstVisit(false);
      }
    } else {
      // Primera visita, establecer tiempo de inicio
      startTime = Date.now();
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + (INVITATION_DURATION * 1000));
      document.cookie = `invite_start_time=${startTime}; expires=${expirationDate.toUTCString()}; path=/`;
    }

    // Iniciar countdown
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setIsExpired(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleEnterPayment = () => {
    if (hasClicked) return;
    
    // Marcar como clickeado para que no se pueda volver a presionar
    setHasClicked(true);
    
    // Establecer cookie permanente para recordar que ya se hizo clic
    const permanentDate = new Date();
    permanentDate.setTime(permanentDate.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 año
    document.cookie = `invite_clicked=true; expires=${permanentDate.toUTCString()}; path=/`;
    
    // Mostrar mensaje y redirigir
    alert('Ingresá al link para pagar https://mpago.la/2JbCFvE');
    window.open('https://mpago.la/2JbCFvE', '_blank');
  };

  if (isExpired) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif' }}>
        <Card className="w-full max-w-sm mx-auto bg-black/95 border border-white/10 shadow-2xl">
          <CardHeader className="text-center pb-6 pt-8 px-6">
            <div className="mx-auto w-16 h-16 rounded-full mb-6 border-2 border-white/20 overflow-hidden relative">
              <img 
                src={profileImage} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-light text-white mb-4">
              Invitación Expirada
            </CardTitle>
          </CardHeader>
          
          <CardContent className="text-center px-6 pb-8">
            <div className="bg-black/30 p-6 rounded-2xl border border-white/10">
              <p className="text-white text-base mb-4 font-light">
                Esta invitación ha expirado.
              </p>
              <p className="text-white/70 text-sm font-light">
                Las invitaciones solo son válidas por 1 minuto.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif' }}>
      <Card className="w-full max-w-sm sm:max-w-md mx-auto bg-black/95 border border-white/10 shadow-2xl">
        <CardHeader className="text-center pb-6 pt-8 px-6 sm:px-8">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full mb-6 border-2 border-white/20 overflow-hidden">
            <img 
              src={profileImage} 
              alt="Profile" 
              className="w-full h-full object-cover"
            />
          </div>
          
          <CardTitle className="text-xl sm:text-2xl font-light text-white mb-6 leading-tight">
            Invitación Exclusiva
          </CardTitle>
          
          <div className="flex items-center justify-center gap-3 bg-black/50 rounded-2xl p-3 sm:p-4 border border-white/15">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            <span className="text-lg sm:text-xl font-light text-white tracking-wide" style={{ fontFamily: 'SF Mono, Monaco, Consolas, monospace' }}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </CardHeader>
        
        <CardContent className="text-center space-y-5 sm:space-y-6 px-6 sm:px-8 pb-8">
          <div className="bg-black/30 p-5 sm:p-6 rounded-2xl border border-white/10">
            <div className="flex items-center justify-center mb-4">
              <h3 className="font-light text-white text-base sm:text-lg">
                ¡Felicidades!
              </h3>
            </div>
            <p className="text-white/90 text-sm sm:text-base leading-relaxed font-light">
              Te invito a mi grupo exclusivo donde comparto momentos del día y para ser parte de mi comunidad.
            </p>
          </div>
          
          <Button 
            onClick={handleEnterPayment}
            disabled={hasClicked}
            className={`w-full font-light py-3 sm:py-4 text-base sm:text-lg transition-all duration-300 rounded-2xl border ${
              hasClicked 
                ? 'bg-gray-800/50 border-gray-600/30 cursor-not-allowed text-gray-500' 
                : 'bg-black/40 hover:bg-black/60 border-white/20 hover:border-white/40 text-white'
            }`}
          >
            {hasClicked ? (
              <>
                <Lock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Ya utilizado
              </>
            ) : (
              <>
                <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Ingresar
              </>
            )}
          </Button>

          {hasClicked && (
            <div className="bg-black/30 p-4 rounded-2xl border border-white/10">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center">
                  <span className="text-black text-xs sm:text-sm font-bold">✓</span>
                </div>
                <p className="text-white text-sm sm:text-base font-light">
                  Link de pago enviado
                </p>
              </div>
              <p className="text-white/70 text-xs sm:text-sm font-light">
                El botón ya no puede ser utilizado nuevamente
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Invite;